import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Typography, List, ListItem, ListItemText, Box, Table, TableContainer, TableBody, TableCell, TableHead, TableRow, IconButton, TextField, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Select, MenuItem,
} from '@mui/material';
import { green, red } from '@mui/material/colors';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import logo from '../../../../assets/Logo.png';

const tests = [
  'Visual as per QAP', 'Bifaciality as per QAP', 'EL Data as per QAP', 'IR Test', 'HV Test', 'Continuity Test', 'Wet Leakage Test', 'LID Test', 'PID Test', 'Robustness Test', 'Mechanical Load Test',
];

const StepTwo = () => {
  const [results, setResults] = useState(tests.map(() => ({ status: null, comment: '' })));
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [statusMessage, setStatusMessage] = useState(null);
  const [uniqueId, setUniqueId] = useState('');
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [selectAllStatus, setSelectAllStatus] = useState('');
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const projectId = localStorage.getItem('selectedProject');
        const response = await axios.post(
          `${baseURL}get_supplier_names`,
          { projectId }
        );
        setSuppliers(response.data);
      } catch (error) {
        console.error('Error fetching suppliers:', error);
      }
    };
    fetchSuppliers();

    // Retrieve unique ID from sessionStorage
    const uploadedFile = sessionStorage.getItem('uploadedFile');
    if (uploadedFile) {
      // Remove file extension
      const fileNameWithoutExtension = uploadedFile.replace(/\.[^/.]+$/, '');
      setUniqueId(fileNameWithoutExtension);
    }
  }, []);

  const handleStatusChange = (index, status) => {
    setResults((prev) =>
      prev.map((item, i) => (i === index ? { ...item, status } : item))
    );
  };

  const handleCommentChange = (index, comment) => {
    setResults((prev) =>
      prev.map((item, i) => (i === index ? { ...item, comment } : item))
    );
  };

  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setUploadedFiles((prev) => [...prev, ...files]);
  };

  const handleSubmit = async () => {
    // Check if unique ID exists
    if (!uniqueId) {
      setStatusMessage('No file uploaded in Step 1. Please go to Step 1 and reupload the file.');
      return;
    }

    // Check if all failed tests have a comment
    const failedTestsWithoutComment = results.some(
      (result) => result.status === 'fail' && !result.comment.trim()
    );
    if (failedTestsWithoutComment) {
      setStatusMessage('Please fill the comment for all failed tests.');
      return;
    }

    const allPass = results.every((r) => r.status === 'pass');
    const hasFail = results.some((r) => r.status === 'fail');
    let finalStatusMessage;

    if (allPass) {
      finalStatusMessage = 'Approved';
      setStatusMessage(finalStatusMessage);
    } else if (hasFail) {
      setShowDialog(true);
      return;
    } else {
      finalStatusMessage = 'Rejected';
      setStatusMessage(finalStatusMessage);
    }

    try {
      await uploadData(finalStatusMessage, uniqueId);
      generateReport(finalStatusMessage, uniqueId);
    } catch (error) {
      console.error('Error during submission:', error);
      setStatusMessage('Submission failed. Please try again.');
    }
  };

  // Inside uploadData() – change only the FormData part
  const uploadData = async (finalStatusMessage, uniqueId) => {
    const projectId = localStorage.getItem('selectedProject');
    const currentDate = new Date().toISOString().split('T')[0];
    const formData = new FormData();

    formData.append('projectId', projectId);
    formData.append('supplierName', selectedSupplier);
    formData.append('date', currentDate);
    formData.append('uniqueId', uniqueId);
    formData.append('status', finalStatusMessage);

    // Append ALL uploaded files with the SAME key name
    uploadedFiles.forEach(file => {
      formData.append('uploadedDocument', file);  // <-- same key, multiple values
    });

    // Generate PDF blob
    const doc = new jsPDF();
    // ... (your PDF generation code) ...
    const pdfBlob = doc.output('blob');
    formData.append('generatedPdf', pdfBlob, `${uniqueId}_PDI_TPI_Report.pdf`);

    try {
      const response = await axios.post(
        `${baseURL}upload_pdi_witness`,  // <-- CORRECT ENDPOINT
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    } catch (error) {
      console.error('Upload error:', error);
      setStatusMessage('Upload failed. Please try again.');
    }
  };

  const handleDialogClose = async (action) => {
    setShowDialog(false);

    let finalStatusMessage;

    if (action === 'approve') {
      finalStatusMessage = 'Approved with Failed Tests';
      setStatusMessage(finalStatusMessage);
    } else if (action === 'reject') {
      finalStatusMessage = 'Rejected';
      setStatusMessage(finalStatusMessage);
    }

    try {
      await uploadData(finalStatusMessage, uniqueId);
      generateReport(finalStatusMessage, uniqueId);
    } catch (error) {
      console.error('Error during submission:', error);
      setStatusMessage('Submission failed. Please try again.');
    }
  };

  const generateReport = (status, reportId) => {
    const doc = new jsPDF('l', 'mm', 'a4');

    const header = () => {
      doc.addImage(logo, 'PNG', 10, 10, 50, 15);
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.text('PDI Review - TPI Report', 70, 20);
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.text(`Status: ${status}`, 70, 28);
      doc.text(`Unique ID: ${reportId}`, 70, 34);
      doc.text(`Generated On: ${new Date().toLocaleDateString()}`, 70, 40);
    };

    const footer = (pageNumber) => {
      const footerText = `Page ${pageNumber} of { totalPages }`;
      doc.setFontSize(10);
      doc.setTextColor(100);
      doc.text(footerText, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, {
        align: 'center',
      });
    };

    header();

    const testResults = results.map((result, index) => {
      const testStatus = result.status === 'pass' ? 'Pass' : result.status === 'fail' ? 'Fail' : 'N/A';
      return [tests[index], testStatus, result.comment || 'None'];
    });

    doc.autoTable({
      head: [['Test', 'Status', 'Comments']],
      body: testResults,
      startY: 50,
      styles: { cellPadding: 3, fontSize: 10, valign: 'middle' },
      headStyles: { fillColor: [46, 204, 113], textColor: [255, 255, 255], halign: 'center' },
      alternateRowStyles: { fillColor: [240, 240, 240] },
      theme: 'grid',
    });

    const witnessTableData = JSON.parse(sessionStorage.getItem('witnessTableData')) || [];

    if (witnessTableData.length > 0) {
      const witnessTableBody = witnessTableData.map(row => [
        row['No.'],
        row['Pallet No'],
        row['Module_No'],
        row['Binning'],
        row['Efficiency (%)'],
        row['FF'],
        row['Pmax'],
        row['Vmpp'],
        row['Isc'],
        row['Voc'],
        row['Flash Date'],
        row['Line'],
        row['PM_diff']
      ]);

      doc.autoTable({
        head: [['No.', 'Pallet No', 'Module No', 'Binning', 'Eff', 'FF', 'Pmax', 'VMP', 'ISC', 'VOC', 'Flash Time', 'Line', 'PM Diff']],
        body: witnessTableBody,
        startY: doc.lastAutoTable.finalY + 10,
        styles: { cellPadding: 3, fontSize: 8, valign: 'middle' },
        headStyles: { fillColor: [46, 204, 113], textColor: [255, 255, 255], halign: 'center' },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        theme: 'grid',
      });
    }

    const totalPagesExp = '{ totalPages }';
    const pageNumber = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageNumber; i++) {
      doc.setPage(i);
      footer(i);
    }

    doc.save(`${reportId}_PDI_TPI_Report.pdf`);
  };

  const selectAll = (status) => {
    setResults(results.map(result => ({ ...result, status })));
    setSelectAllStatus(status);
  };

  return (
    <Box sx={{ py: 2, px: 4, border: '1px solid #e0e0e0', borderRadius: 2 }}>
      <Typography variant="h5" fontWeight="bold" gutterBottom>Step 2: Test Results and Document Upload</Typography>

      <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
        <Select
          value={selectedSupplier}
          onChange={(e) => setSelectedSupplier(e.target.value)}
          displayEmpty
          fullWidth
          sx={{ flex: 1 }}
        >
          <MenuItem value="">
            <em>Select Supplier</em>
          </MenuItem>
          {suppliers.map((supplier) => (
            <MenuItem key={supplier.name} value={supplier.name}>
              {supplier.name}
            </MenuItem>
          ))}
        </Select>

        <Select
          value={selectAllStatus}
          onChange={(e) => {
            const status = e.target.value;
            selectAll(status);
          }}
          displayEmpty
          fullWidth
          sx={{ flex: 1 }}
        >
          <MenuItem value=""><em>Select All Status</em></MenuItem>
          <MenuItem value="pass">Select All Pass</MenuItem>
          <MenuItem value="fail">Select All Fail</MenuItem>
        </Select>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Test</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Comments</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {tests.map((test, index) => (
              <TableRow key={test}>
                <TableCell>{test}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleStatusChange(index, results[index].status === 'pass' ? null : 'pass')}>
                    {results[index].status === 'pass' ? <CheckCircleIcon sx={{ color: green[500] }} /> : <Box sx={{ width: 24, height: 24, border: '1px solid #e0e0e0', borderRadius: '50%' }} />}
                  </IconButton>
                  <IconButton onClick={() => handleStatusChange(index, results[index].status === 'fail' ? null : 'fail')}>
                    {results[index].status === 'fail' ? <CancelIcon sx={{ color: red[500] }} /> : <Box sx={{ width: 24, height: 24, border: '1px solid #e0e0e0', borderRadius: '50%' }} />}
                  </IconButton>
                </TableCell>
                <TableCell>
                  <TextField
                    value={results[index].comment}
                    onChange={(e) => handleCommentChange(index, e.target.value)}
                    placeholder="Add comment"
                    fullWidth
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {statusMessage && (
        <Box sx={{ mt: 2, p: 2, bgcolor: statusMessage.includes('Approved') ? green[100] : red[100], borderRadius: 1 }}>
          <Typography variant="h6" fontWeight="bold" color={statusMessage.includes('Approved') ? green[800] : red[800]}>
            {statusMessage} {uniqueId ? `- Unique ID: ${uniqueId}` : ''}
          </Typography>
        </Box>
      )}

      <Box sx={{ mt: 3 }}>
        <Button variant="contained" component="label" startIcon={<FileUploadIcon />} sx={{ backgroundColor: '#424752', '&:hover': { backgroundColor: '#2f353c' } }}>
          Upload Document
          <input hidden type="file" multiple onChange={handleFileUpload} />
        </Button>
        {uploadedFiles.length > 0 ? (
          <List>
            {uploadedFiles.map((file, index) => (
              <ListItem key={index} disablePadding>
                <ListItemText primary={file.name} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="textSecondary">No files uploaded yet</Typography>
        )}
      </Box>

      <Box>
        {statusMessage && (
          <Typography sx={{ color: red[500], mt: 2 }}>
            {statusMessage}
          </Typography>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          sx={{ backgroundColor: '#424752', '&:hover': { backgroundColor: '#2f353c' } }}
        >
          Submit
        </Button>
      </Box>

      <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
        <DialogTitle>Failed Tests</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Some tests have failed. Would you like to approve despite the failures?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => handleDialogClose('reject')}>Reject</Button>
          <Button onClick={() => handleDialogClose('approve')}>Approve</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default StepTwo;



// import React, { useState, useEffect } from 'react';
// import axios from 'axios';
// import {
//   Typography, List, ListItem, ListItemText, Box, Table, TableContainer, TableBody, TableCell, TableHead, TableRow, IconButton, TextField, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Select, MenuItem,
// } from '@mui/material';
// import { green, red } from '@mui/material/colors';
// import CheckCircleIcon from '@mui/icons-material/CheckCircle';
// import CancelIcon from '@mui/icons-material/Cancel';
// import FileUploadIcon from '@mui/icons-material/FileUpload';
// import { jsPDF } from 'jspdf';
// import 'jspdf-autotable';
// import logo from '../../../../assets/Logo.png';

// const tests = [
//   'Visual as per QAP', 'Bifaciality as per QAP', 'EL Data as per QAP', 'IR Test', 'HV Test', 'Continuity Test', 'Wet Leakage Test', 'LID Test', 'PID Test', 'Robustness Test', 'Mechanical Load Test',
// ];

// const StepTwo = () => {
//   const [results, setResults] = useState(tests.map(() => ({ status: null, comment: '' })));
//   const [uploadedFiles, setUploadedFiles] = useState([]);
//   const [showDialog, setShowDialog] = useState(false);
//   const [statusMessage, setStatusMessage] = useState(null);
//   const [uniqueId, setUniqueId] = useState('');
//   const [suppliers, setSuppliers] = useState([]);
//   const [selectedSupplier, setSelectedSupplier] = useState('');
//   const [selectAllStatus, setSelectAllStatus] = useState('');
//   const baseURL = process.env.REACT_APP_API_BASE_URL;

//   useEffect(() => {
//     const fetchSuppliers = async () => {
//       try {
//         const projectId = localStorage.getItem('selectedProject');
//         const response = await axios.post(
//           `${baseURL}get_supplier_names`,
//           { projectId }
//         );
//         setSuppliers(response.data);
//       } catch (error) {
//         console.error('Error fetching suppliers:', error);
//       }
//     };
//     fetchSuppliers();

//     // Retrieve unique ID from sessionStorage
//     const uploadedFile = sessionStorage.getItem('uploadedFile');
//     if (uploadedFile) {
//       // Remove file extension
//       const fileNameWithoutExtension = uploadedFile.replace(/\.[^/.]+$/, '');
//       setUniqueId(fileNameWithoutExtension);
//     }
//   }, []);

//   const handleStatusChange = (index, status) => {
//     setResults((prev) =>
//       prev.map((item, i) => (i === index ? { ...item, status } : item))
//     );
//   };

//   const handleCommentChange = (index, comment) => {
//     setResults((prev) =>
//       prev.map((item, i) => (i === index ? { ...item, comment } : item))
//     );
//   };

//   const handleFileUpload = (event) => {
//     const files = Array.from(event.target.files);
//     setUploadedFiles((prev) => [...prev, ...files]);
//   };

//   const handleSubmit = async () => {
//     // Check if unique ID exists
//     if (!uniqueId) {
//       setStatusMessage('No file uploaded in Step 1. Please go to Step 1 and reupload the file.');
//       return;
//     }

//     // Check if all failed tests have a comment
//     const failedTestsWithoutComment = results.some(
//       (result) => result.status === 'fail' && !result.comment.trim()
//     );
//     if (failedTestsWithoutComment) {
//       setStatusMessage('Please fill the comment for all failed tests.');
//       return;
//     }

//     const allPass = results.every((r) => r.status === 'pass');
//     const hasFail = results.some((r) => r.status === 'fail');
//     let finalStatusMessage;

//     if (allPass) {
//       finalStatusMessage = 'Approved';
//       setStatusMessage(finalStatusMessage);
//     } else if (hasFail) {
//       setShowDialog(true);
//       return;
//     } else {
//       finalStatusMessage = 'Rejected';
//       setStatusMessage(finalStatusMessage);
//     }

//     try {
//       await uploadData(finalStatusMessage, uniqueId);
//       generateReport(finalStatusMessage, uniqueId);
//     } catch (error) {
//       console.error('Error during submission:', error);
//       setStatusMessage('Submission failed. Please try again.');
//     }
//   };

//   const uploadData = async (finalStatusMessage, uniqueId) => {
//     const projectId = localStorage.getItem('selectedProject');
//     const currentDate = new Date().toISOString().split('T')[0];
//     const formData = new FormData();

//     formData.append('projectId', projectId);
//     formData.append('supplierName', selectedSupplier);
//     formData.append('date', currentDate);
//     uploadedFiles.forEach((file) => formData.append('uploadedDocument', file));
//     formData.append('uniqueId', uniqueId);
//     formData.append('status', finalStatusMessage);

//     const doc = new jsPDF();
//     doc.addImage(logo, 'PNG', 10, 10, 50, 15);
//     doc.setFontSize(16);
//     doc.text('PDI Review - TPI Report', 10, 30);

//     const testResults = results.map((result, index) => {
//       const testStatus = result.status === 'pass' ? 'Pass' : result.status === 'fail' ? 'Fail' : 'N/A';
//       return [tests[index], testStatus, result.comment || 'None'];
//     });

//     doc.autoTable({
//       head: [['Test', 'Status', 'Comments']],
//       body: testResults,
//       startY: 70,
//       styles: { cellPadding: 3, fontSize: 10 },
//       headStyles: { fillColor: [46, 204, 113], textColor: [255, 255, 255] },
//       theme: 'striped',
//     });

//     const pdfBlob = doc.output('blob');
//     formData.append('generatedPdf', pdfBlob, `${uniqueId}_PDI_TPI_Report.pdf`);

//     try {
//       await axios.post(
//         `${baseURL}upload_pdi_witness`,
//         formData,
//         { headers: { 'Content-Type': 'multipart/form-data' } }
//       );
//     } catch (error) {
//       console.error('Error uploading data:', error);
//     }
//   };

//   const handleDialogClose = async (action) => {
//     setShowDialog(false);

//     let finalStatusMessage;

//     if (action === 'approve') {
//       finalStatusMessage = 'Approved with Failed Tests';
//       setStatusMessage(finalStatusMessage);
//     } else if (action === 'reject') {
//       finalStatusMessage = 'Rejected';
//       setStatusMessage(finalStatusMessage);
//     }

//     try {
//       await uploadData(finalStatusMessage, uniqueId);
//       generateReport(finalStatusMessage, uniqueId);
//     } catch (error) {
//       console.error('Error during submission:', error);
//       setStatusMessage('Submission failed. Please try again.');
//     }
//   };

//   const generateReport = (status, reportId) => {
//     const doc = new jsPDF('l', 'mm', 'a4');

//     const header = () => {
//       doc.addImage(logo, 'PNG', 10, 10, 50, 15);
//       doc.setFontSize(18);
//       doc.setFont(undefined, 'bold');
//       doc.text('PDI Review - TPI Report', 70, 20);
//       doc.setFontSize(12);
//       doc.setFont(undefined, 'normal');
//       doc.text(`Status: ${status}`, 70, 28);
//       doc.text(`Unique ID: ${reportId}`, 70, 34);
//       doc.text(`Generated On: ${new Date().toLocaleDateString()}`, 70, 40);
//     };

//     const footer = (pageNumber) => {
//       const footerText = `Page ${pageNumber} of { totalPages }`;
//       doc.setFontSize(10);
//       doc.setTextColor(100);
//       doc.text(footerText, doc.internal.pageSize.width / 2, doc.internal.pageSize.height - 10, {
//         align: 'center',
//       });
//     };

//     header();

//     const testResults = results.map((result, index) => {
//       const testStatus = result.status === 'pass' ? 'Pass' : result.status === 'fail' ? 'Fail' : 'N/A';
//       return [tests[index], testStatus, result.comment || 'None'];
//     });

//     doc.autoTable({
//       head: [['Test', 'Status', 'Comments']],
//       body: testResults,
//       startY: 50,
//       styles: { cellPadding: 3, fontSize: 10, valign: 'middle' },
//       headStyles: { fillColor: [46, 204, 113], textColor: [255, 255, 255], halign: 'center' },
//       alternateRowStyles: { fillColor: [240, 240, 240] },
//       theme: 'grid',
//     });

//     const witnessTableData = JSON.parse(sessionStorage.getItem('witnessTableData')) || [];

//     if (witnessTableData.length > 0) {
//       const witnessTableBody = witnessTableData.map(row => [
//         row['No.'],
//         row['Pallet No'],
//         row['Module_No'],
//         row['Binning'],
//         row['Efficiency (%)'],
//         row['FF'],
//         row['Pmax'],
//         row['Vmpp'],
//         row['Isc'],
//         row['Voc'],
//         row['Flash Date'],
//         row['Line'],
//         row['PM_diff']
//       ]);

//       doc.autoTable({
//         head: [['No.', 'Pallet No', 'Module No', 'Binning', 'Eff', 'FF', 'Pmax', 'VMP', 'ISC', 'VOC', 'Flash Time', 'Line', 'PM Diff']],
//         body: witnessTableBody,
//         startY: doc.lastAutoTable.finalY + 10,
//         styles: { cellPadding: 3, fontSize: 8, valign: 'middle' },
//         headStyles: { fillColor: [46, 204, 113], textColor: [255, 255, 255], halign: 'center' },
//         alternateRowStyles: { fillColor: [240, 240, 240] },
//         theme: 'grid',
//       });
//     }

//     const totalPagesExp = '{ totalPages }';
//     const pageNumber = doc.internal.getNumberOfPages();
//     for (let i = 1; i <= pageNumber; i++) {
//       doc.setPage(i);
//       footer(i);
//     }

//     doc.save(`${reportId}_PDI_TPI_Report.pdf`);
//   };

//   const selectAll = (status) => {
//     setResults(results.map(result => ({ ...result, status })));
//     setSelectAllStatus(status);
//   };

//   return (
//     <Box sx={{ py: 2, px: 4, border: '1px solid #e0e0e0', borderRadius: 2 }}>
//       <Typography variant="h5" fontWeight="bold" gutterBottom>Step 2: Test Results and Document Upload</Typography>

//       <Box sx={{ mb: 2, display: 'flex', gap: 2 }}>
//         <Select
//           value={selectedSupplier}
//           onChange={(e) => setSelectedSupplier(e.target.value)}
//           displayEmpty
//           fullWidth
//           sx={{ flex: 1 }}
//         >
//           <MenuItem value="">
//             <em>Select Supplier</em>
//           </MenuItem>
//           {suppliers.map((supplier) => (
//             <MenuItem key={supplier.name} value={supplier.name}>
//               {supplier.name}
//             </MenuItem>
//           ))}
//         </Select>

//         <Select
//           value={selectAllStatus}
//           onChange={(e) => {
//             const status = e.target.value;
//             selectAll(status);
//           }}
//           displayEmpty
//           fullWidth
//           sx={{ flex: 1 }}
//         >
//           <MenuItem value=""><em>Select All Status</em></MenuItem>
//           <MenuItem value="pass">Select All Pass</MenuItem>
//           <MenuItem value="fail">Select All Fail</MenuItem>
//         </Select>
//       </Box>

//       <TableContainer>
//         <Table>
//           <TableHead>
//             <TableRow>
//               <TableCell>Test</TableCell>
//               <TableCell>Status</TableCell>
//               <TableCell>Comments</TableCell>
//             </TableRow>
//           </TableHead>
//           <TableBody>
//             {tests.map((test, index) => (
//               <TableRow key={test}>
//                 <TableCell>{test}</TableCell>
//                 <TableCell>
//                   <IconButton onClick={() => handleStatusChange(index, results[index].status === 'pass' ? null : 'pass')}>
//                     {results[index].status === 'pass' ? <CheckCircleIcon sx={{ color: green[500] }} /> : <Box sx={{ width: 24, height: 24, border: '1px solid #e0e0e0', borderRadius: '50%' }} />}
//                   </IconButton>
//                   <IconButton onClick={() => handleStatusChange(index, results[index].status === 'fail' ? null : 'fail')}>
//                     {results[index].status === 'fail' ? <CancelIcon sx={{ color: red[500] }} /> : <Box sx={{ width: 24, height: 24, border: '1px solid #e0e0e0', borderRadius: '50%' }} />}
//                   </IconButton>
//                 </TableCell>
//                 <TableCell>
//                   <TextField
//                     value={results[index].comment}
//                     onChange={(e) => handleCommentChange(index, e.target.value)}
//                     placeholder="Add comment"
//                     fullWidth
//                   />
//                 </TableCell>
//               </TableRow>
//             ))}
//           </TableBody>
//         </Table>
//       </TableContainer>

//       {statusMessage && (
//         <Box sx={{ mt: 2, p: 2, bgcolor: statusMessage.includes('Approved') ? green[100] : red[100], borderRadius: 1 }}>
//           <Typography variant="h6" fontWeight="bold" color={statusMessage.includes('Approved') ? green[800] : red[800]}>
//             {statusMessage} {uniqueId ? `- Unique ID: ${uniqueId}` : ''}
//           </Typography>
//         </Box>
//       )}

//       <Box sx={{ mt: 3 }}>
//         <Button variant="contained" component="label" startIcon={<FileUploadIcon />} sx={{ backgroundColor: '#424752', '&:hover': { backgroundColor: '#2f353c' } }}>
//           Upload Document
//           <input hidden type="file" multiple onChange={handleFileUpload} />
//         </Button>
//         {uploadedFiles.length > 0 ? (
//           <List>
//             {uploadedFiles.map((file, index) => (
//               <ListItem key={index} disablePadding>
//                 <ListItemText primary={file.name} />
//               </ListItem>
//             ))}
//           </List>
//         ) : (
//           <Typography variant="body2" color="textSecondary">No files uploaded yet</Typography>
//         )}
//       </Box>

//       <Box>
//         {statusMessage && (
//           <Typography sx={{ color: red[500], mt: 2 }}>
//             {statusMessage}
//           </Typography>
//         )}

//         <Button
//           variant="contained"
//           onClick={handleSubmit}
//           sx={{ backgroundColor: '#424752', '&:hover': { backgroundColor: '#2f353c' } }}
//         >
//           Submit
//         </Button>
//       </Box>

//       <Dialog open={showDialog} onClose={() => setShowDialog(false)}>
//         <DialogTitle>Failed Tests</DialogTitle>
//         <DialogContent>
//           <DialogContentText>
//             Some tests have failed. Would you like to approve despite the failures?
//           </DialogContentText>
//         </DialogContent>
//         <DialogActions>
//           <Button onClick={() => handleDialogClose('reject')}>Reject</Button>
//           <Button onClick={() => handleDialogClose('approve')}>Approve</Button>
//         </DialogActions>
//       </Dialog>
//     </Box>
//   );
// };

// export default StepTwo;
